import { coalesceSsrBaseData } from '~/utils/ssrBaseDataFetch';
import { LANGUAGES } from "~/constants/language";
import type { Language } from "~/constants/language";
import { resolveTenantHost } from '~/utils/resolveTenantHost';

const REQUEST_TIMEOUT_SECS = 15 * 1000;

export default defineNuxtPlugin((nuxtApp) => {
  // Bind stores to Nuxt's Pinia instance explicitly. Nitro can invoke an
  // API hook from a detached SWR refresh where Pinia's implicit injection
  // context is unavailable; useStore() then crashes while reading `_s`.
  const pinia = nuxtApp.$pinia;
  const statusStore = useStatusStore(pinia);
  const reqUrl = useRequestURL();
  const config = useRuntimeConfig();
  const route = useRoute();
  // Capture the tenant context before cleaning the preview-only query from
  // the visible URL. Some client runtimes update the reactive route query
  // immediately after history.replaceState().
  const previewQueryOrigin = String(route.query['x-origin'] || '');

  // `x-origin` was a legacy tenant-routing workaround. Keep old links
  // functional through the normal Host/header resolution, but remove the
  // obsolete parameter from the visible URL after hydration.
  if (import.meta.client && route.query['x-origin']) {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('x-origin');
    window.history.replaceState(
      window.history.state,
      '',
      `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`,
    );
  }

  // URL path is the authoritative locale source on SSR — avoids triggering the language store
  // before $api is provided (which would capture $api as undefined in the store's setup).
  const getLocale = (): string => {
    const [, pathLocale] = route.path.split("/");
    if ((LANGUAGES as readonly string[]).includes(pathLocale as Language)) {
      return pathLocale;
    }
    // When there's no path prefix, the language store (fetched before menu)
    // already knows the correct main language. Use it instead of a stale cookie.
    try {
      const langStore = useLanguageStore(pinia);
      if (langStore.main?.code) {
        return langStore.main.code;
      }
    } catch {
      // Store not available yet (e.g. language fetch itself)
    }
    return (nuxtApp.$i18n as any)?.getLocaleCookie?.() || "en";
  };

  // On SSR, determine the hostname used for multi-tenant API routing.
  // Priority:
  // 1. x-origin query     – preview-only explicit tenant override
  // 2. X-Original-Host    – custom domain explicit override
  // 3. X-Forwarded-Host   – proxy-preserved viewer host fallback
  // 4. Host               – only when it is not an infrastructure host
  // 5. Origin / Referer   – last-resort fallbacks
  const headers = useRequestHeaders();
  const parseHostFromUrl = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    return url.startsWith("http") ? url : `https://${url}`;
  };

  const accessToken = route.query?.accessToken;
  const requestHost = (headers["host"] as string) || reqUrl.host;
  const originalHost = headers["x-original-host"] as string | undefined;
  const forwardedHost = headers["x-forwarded-host"] as string | undefined;
  const originHost = parseHostFromUrl(headers["origin"] as string);
  const refererHost = parseHostFromUrl(headers["referer"] as string);

  // A preview URL explicitly carries its tenant in x-origin. Prefer it when
  // present so an older static artifact/config cannot accidentally route API
  // requests through the shared preview CloudFront host.
  const previewQueryHost = config.public.PREVIEW_STATIC && previewQueryOrigin
    ? resolveTenantHost([parseHostFromUrl(previewQueryOrigin)])
    : undefined;

  // Forwarded viewer hosts must win over a proxy's origin Host value.
  const tenantHost = previewQueryHost || resolveTenantHost([
    originalHost,
    forwardedHost,
    requestHost,
    originHost,
    refererHost,
  ]);
  const invalidTenantHost = import.meta.server && process.env.NODE_ENV === 'production' && !tenantHost;

  // A direct ALB/IP request is not a tenant request. Mark it in shared SSR
  // state so initialization can stop before generating a burst of guaranteed
  // origin-not-found API calls.
  const invalidTenantState = useState('invalid-tenant-host', () => invalidTenantHost);
  if (import.meta.server) {
    invalidTenantState.value = invalidTenantHost;
  }

  const customerDomain = invalidTenantHost ? "" : tenantHost;
  const publicHost = invalidTenantHost ? "" : (customerDomain || requestHost || forwardedHost);

  const apiBaseURL = import.meta.server ? config.API_URL : config.public.API_URL;
  const serverApiHeaders = import.meta.server && config.API_HOST_SSR
    ? { Host: config.API_HOST_SSR }
    : {};

  const apiClient = $fetch.create({
    timeout: REQUEST_TIMEOUT_SECS,
    ...(import.meta.server ? { retry: 0 } : {}),
    baseURL: apiBaseURL,
    headers: {
      ...serverApiHeaders,
      "X-Original-Host": customerDomain,
      "X-Origin": publicHost,
      "Origin": customerDomain ? `${reqUrl.protocol}//${customerDomain}` : "",
    },
    onRequest({ options }) {
      options.headers.set("Accept-Language", getLocale());
      statusStore.updatePreloading(true);
    },
    onResponse() {
      statusStore.updatePreloading(false);
    },
    onRequestError() {
      statusStore.updatePreloading(false);
    },
    onResponseError() {
      statusStore.updatePreloading(false);
    },
  });

  const api = import.meta.server
    ? coalesceSsrBaseData(apiClient, {
        tenant: customerDomain || '',
        baseURL: String(apiBaseURL || ''),
        locale: getLocale,
        bypass: Boolean(accessToken || route.query.id || route.query.themeStyle
          || headers.authorization || headers.cookie || config.public.PREVIEW_STATIC),
      })
    : apiClient;

  const apiBOF = $fetch.create({
    timeout: REQUEST_TIMEOUT_SECS,
    baseURL: `${apiBaseURL}/bof`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    onRequest({ options }) {
      options.headers.set("X-Original-Host", customerDomain);
      options.headers.set("X-Origin", publicHost);
      options.headers.set("Accept-Language", getLocale());
      statusStore.updatePreloading(true);
    },
    onResponse() {
      statusStore.updatePreloading(false);
    },
    onRequestError() {
      statusStore.updatePreloading(false);
    },
    onResponseError() {
      statusStore.updatePreloading(false);
    },
  });

  return {
    provide: {
      api,
      apiBOF,
    },
  };
});
